// frontend/src/contexts/ThemeContext.jsx
// Add accessibility settings and i18n integration to the theme context

import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import utilities
import { 
  getCookie, 
  setCrossDomainCookie, 
  COOKIE_KEYS, 
  getThemePreference
} from '../utils/cookie-utils';

// Import theme creation function
import { createTheme } from '../theme/create-theme';
import { schemeConfig } from '../utils/color-scheme-script';

// Import translation resources
import enTranslations from '../translations/en.json';
import nlTranslations from '../translations/nl.json';
import frTranslations from '../translations/fr.json';
import deTranslations from '../translations/de.json';
import wvlTranslations from '../translations/wvl.json'; // West-Vlaams dialect
import avlTranslations from '../translations/avl.json'; // Antwerps dialect

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      nl: { translation: nlTranslations },
      fr: { translation: frTranslations },
      de: { translation: deTranslations },
      wvl: { translation: wvlTranslations }, // West-Vlaams dialect
      avl: { translation: avlTranslations }, // Antwerps dialect
    },
    lng: getThemePreference(COOKIE_KEYS.LANGUAGE) || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

// Create context for theme settings - add accessibility settings
const ThemeSettingsContext = createContext({
  themeMode: 'light',
  themeColor: 'default',
  themeLayout: 'default',
  language: 'en',
  direction: 'ltr',
  typography: 'Montserrat, sans-serif',
  // Accessibility settings
  textSize: 'small',
  contrastMode: false,
  reducedMotion: false,
  // Setters
  setThemeMode: () => {},
  setThemeColor: () => {},
  setThemeLayout: () => {},
  setLanguage: () => {},
  setTypography: () => {},
  setTextSize: () => {},
  setContrastMode: () => {},
  setReducedMotion: () => {},
  // i18n integration
  t: () => {},
  changeLanguage: () => {},
});

// Helper function to load Google Fonts dynamically
const loadGoogleFont = (fontFamily) => {
  // Skip if already loaded or if document is not available
  if (typeof document === 'undefined') return;
  
  // Clean font name for use in URL (keep alphanumeric and spaces)
  const cleanFontName = fontFamily.split(',')[0].trim();
  
  // Check if this font is already loaded
  const fontId = `google-font-${cleanFontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(fontId)) return;
  
  // Create a link element to load the font
  const link = document.createElement('link');
  link.id = fontId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${cleanFontName.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;
  
  // Add to document head
  document.head.appendChild(link);
};

// Hook to use theme settings
export const useThemeSettings = () => useContext(ThemeSettingsContext);

// Provider component that manages theme settings
export function ThemeSettingsProvider({ children }) {
  // Initialize state from cookies or defaults
  const [themeMode, setThemeModeState] = useState(getThemePreference(COOKIE_KEYS.THEME_MODE));
  const [themeColor, setThemeColorState] = useState(getThemePreference(COOKIE_KEYS.THEME_COLOR));
  const [themeLayout, setThemeLayoutState] = useState(getThemePreference(COOKIE_KEYS.THEME_LAYOUT));
  const [language, setLanguageState] = useState(getThemePreference(COOKIE_KEYS.LANGUAGE));
  const [typography, setTypographyState] = useState(getThemePreference(COOKIE_KEYS.TYPOGRAPHY));
  const [direction, setDirection] = useState('ltr');
  
  // Accessibility states
  const [textSize, setTextSizeState] = useState(getThemePreference(COOKIE_KEYS.TEXT_SIZE) || 'medium');
  const [contrastMode, setContrastModeState] = useState(getThemePreference(COOKIE_KEYS.CONTRAST_MODE) === 'true');
  const [reducedMotion, setReducedMotionState] = useState(getThemePreference(COOKIE_KEYS.REDUCED_MOTION) === 'true');

  // Row density: 'comfortable' (default) or 'compact'. Cookie-persisted.
  // Stored locally since COOKIE_KEYS may not include a DENSITY entry yet.
  const [density, setDensityState] = useState(() => {
    try { return localStorage.getItem('caro.portal.density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const setDensity = (d) => {
    setDensityState(d);
    try { localStorage.setItem('caro.portal.density', d); } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-density', d);
    }
  };
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-density', density);
    }
  }, [density]);

  // Update direction when language changes
  useEffect(() => {
    const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
    setDirection(rtlLanguages.includes(language) ? 'rtl' : 'ltr');
  }, [language]);

  // Check system preferences for reduced motion
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion && !getCookie(COOKIE_KEYS.REDUCED_MOTION)) {
        setReducedMotion(true);
      }
    }
  }, []);

  // Honor the OS color-scheme preference on first paint when the user
  // hasn't explicitly picked a theme mode. Also react live if they
  // flip the OS setting, again only when no cookie is set.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      if (!getCookie(COOKIE_KEYS.THEME_MODE)) {
        setThemeModeState(mq.matches ? 'dark' : 'light');
      }
    };
    apply();
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    // Safari < 14 fallback
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  // Kept as a safety net for the paths that set `language` WITHOUT going through
  // setLanguage: the initial mount from the cookie, and a cross-domain cookie change. It is
  // no longer what makes the switcher work -- see setLanguage.
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  // Load the initial font on mount
  useEffect(() => {
    loadGoogleFont(typography);

    // Set initial theme attributes
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', themeMode);
      document.documentElement.setAttribute('data-theme-color', themeColor);
    }
  }, [typography, themeMode, themeColor]);

  // Setter functions that update both state and cookies
  const setThemeMode = (mode) => {
    setThemeModeState(mode);
    setCrossDomainCookie(COOKIE_KEYS.THEME_MODE, mode);
    
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', mode);
    }
  };

  const setThemeColor = (color) => {
    setThemeColorState(color);
    setCrossDomainCookie(COOKIE_KEYS.THEME_COLOR, color);
    
    // Add data-theme-color attribute for CSS variables
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme-color', color);
    }
  };

  const setThemeLayout = (layout) => {
    setThemeLayoutState(layout);
    setCrossDomainCookie(COOKIE_KEYS.THEME_LAYOUT, layout);
  };

  const setLanguage = (lang) => {
    // ⚠ i18n MUST be switched here, not only in the useEffect below.
    //
    // The effect runs AFTER the render that setLanguageState triggers, and
    // i18n.changeLanguage does not itself re-render anything (nothing here subscribes to
    // i18next events -- `t` is a bare reference to i18n.t). So switching language used to
    // paint one render with the PREVIOUS language and then never repaint:
    //
    //   pick French  -> re-render happens while i18n is still 'nl' -> Dutch on screen
    //   pick Dutch   -> re-render happens while i18n is now  'fr'  -> French on screen
    //
    // Which reads exactly as "French and Dutch are swapped". It went unnoticed for as long
    // as every language switch was followed by navigation or a reload; the public
    // onboarding form is the first screen that switches language and stays put.
    //
    // All resources are bundled (no async loading), so this takes effect immediately and
    // the render that follows reads the new language.
    i18n.changeLanguage(lang);
    setLanguageState(lang);
    setCrossDomainCookie(COOKIE_KEYS.LANGUAGE, lang);
  };

  // Language change function that updates both theme and i18n
  const changeLanguage = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const setTypography = (font) => {
    loadGoogleFont(font);
    setTypographyState(font);
    setCrossDomainCookie(COOKIE_KEYS.TYPOGRAPHY, font);
    
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--font-family-primary', font);
      document.body.style.fontFamily = font;
    }
  };

  // Accessibility setters
  const setTextSize = (size) => {
    setTextSizeState(size);
    setCrossDomainCookie(COOKIE_KEYS.TEXT_SIZE, size);
    
    if (typeof document !== 'undefined') {
      // Remove any existing text size classes
      document.documentElement.classList.remove('text-size-small', 'text-size-medium', 'text-size-large', 'text-size-xlarge');
      // Add the new text size class
      document.documentElement.classList.add(`text-size-${size}`);
    }
  };

  const setContrastMode = (enabled) => {
    setContrastModeState(enabled);
    setCrossDomainCookie(COOKIE_KEYS.CONTRAST_MODE, enabled.toString());
    
    if (typeof document !== 'undefined') {
      if (enabled) {
        document.documentElement.setAttribute('data-high-contrast', 'true');
      } else {
        document.documentElement.removeAttribute('data-high-contrast');
      }
    }
  };

  const setReducedMotion = (enabled) => {
    setReducedMotionState(enabled);
    setCrossDomainCookie(COOKIE_KEYS.REDUCED_MOTION, enabled.toString());
    
    if (typeof document !== 'undefined') {
      if (enabled) {
        document.documentElement.setAttribute('data-reduced-motion', 'true');
      } else {
        document.documentElement.removeAttribute('data-reduced-motion');
      }
    }
  };

  // Context value containing all settings and setters
  const contextValue = {
    themeMode,
    themeColor,
    themeLayout,
    language,
    direction,
    typography,
    textSize,
    contrastMode,
    reducedMotion,
    density,
    setThemeMode,
    setThemeColor,
    setThemeLayout,
    setLanguage,
    setTypography,
    setTextSize,
    setContrastMode,
    setReducedMotion,
    setDensity,
    // i18n integration.
    //
    // Wrapped rather than passed as `i18n.t` directly: that is a STABLE reference, so a
    // consumer that memoises on `t` would keep the first language forever. Re-creating it
    // per language change is what makes such a consumer re-evaluate. The wrapper itself is
    // a pass-through -- it must stay one, or i18next's own options stop working.
    t: React.useMemo(() => (...args) => i18n.t(...args), [language]),
    changeLanguage,
  };

  return (
    <ThemeSettingsContext.Provider value={contextValue}>
      {children}
    </ThemeSettingsContext.Provider>
  );
}

// The main ThemeProvider component
export function ThemeProvider({ children }) {
  const settings = useThemeSettings();
  const {
    language,
    direction,
    themeMode,
    themeColor,
    typography,
    textSize,
    contrastMode,
    reducedMotion,
    density,
  } = settings;

  // Create the theme with current settings
  const theme = createTheme(language, {
    direction,
    themeMode,
    themeColor,
    typography,
    textSize,
    contrastMode,
    reducedMotion,
    density,
  });

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <div dir={direction}>{children}</div>
    </MuiThemeProvider>
  );
}

// Combined provider that includes both theme settings and theme provider
export function CombinedThemeProvider({ children }) {
  return (
    <ThemeSettingsProvider>
      <ThemeProvider>{children}</ThemeProvider>
    </ThemeSettingsProvider>
  );
}